import { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, CloudOff, Plus, RotateCw, Search } from "lucide-react";
import { ActionButton, DestinationRow, TopBar } from "./kit";
import { AccessibilityMenu } from "./AccessibilityMenu";
import { AccountMenu } from "./AccountMenu";
import { NotificationsBell } from "./NotificationsBell";
import MatchCardFold from "./MatchCardFold";
import { DeleteMatchDialog } from "./DeleteMatchDialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { useChromelessOverride } from "../context/ChromelessOverrideContext";

/**
 * Issue #1030 (D107, D130, #726) — the redrawn `/performance` list for a
 * player, `MatchListFold.dc.html`, behind `newUi("matches")`. It replaces the
 * shipped page body; `pages/PerformanceTracking.jsx` keeps the data, the
 * filters, the sort, `openEdit` and the delete call, and hands them in as
 * props (nothing is fetched here, no call is made here but the delete the
 * page passes in), so the flag-off page is untouched.
 *
 * Chrome is `SettingsScreen.jsx`'s composition: the navy `TopBar` (bell,
 * contrast and account menus, "Your matches" on the right), then D92's three
 * destination chips (Record, Plan, Grounds) with NONE current. The drawing
 * shows five chips with Matches current; D92 ruled three, and a player is
 * not "on" any of them here (Andreas, D206.5 Q1). Then the title, the lede,
 * the one gold `perf-log-btn`, and the search, filters, count and sort, then
 * the cards.
 *
 * ONE CARD OPEN AT A TIME. `openId` is this screen's own state; opening one
 * card folds whichever was open (the drawing's "the others stay folded").
 *
 * STATES, all five of the artboard's: loading (shapes, the controls stay),
 * empty (nothing logged: search, filters and sort are not drawn, there is
 * nothing to narrow), filtered-empty (D138's own sentence, never the empty
 * list's), failed-to-load (its own card with a retry, never the empty
 * sentence, #271) and delete-confirm (an in-app dialog replacing the native
 * `confirm()`, D55's alert-filled verb).
 *
 * Colours are kit tokens, never `text-slate-*` (`index.css` remaps those with
 * `!important`).
 */
export const LEDE = "Every innings you have logged, newest first. Tap a match to see all its figures.";
export const EMPTY_LINE = "No matches logged yet — your record starts with the first one.";
export const FILTERED_EMPTY_LINE = "Nothing matches this filter. Clear it to see your whole record.";
export const DELETE_FAILED = "That didn't save. The match is still on your record — try again.";

/** The direction button's words. "Newest first" is only true of a date sort. */
export function sortDirLabel(key, dir) {
  if (key === "match_date") return dir === "desc" ? "Newest first" : "Oldest first";
  return dir === "desc" ? "High to low" : "Low to high";
}

/** "12 matches", or "3 of 12 matches" while a search or filter narrows it. */
export function countLine(shown, total, narrowed) {
  const noun = (n) => `match${n === 1 ? "" : "es"}`;
  return narrowed && shown !== total ? `${shown} of ${total} ${noun(total)}` : `${shown} ${noun(shown)}`;
}

const CARD = { background: "var(--nw-surface)", border: "1px solid var(--nw-line)", borderRadius: "var(--nw-radius-card)" };
const CONTROL = { minHeight: 46, background: "var(--nw-surface)", border: "1px solid var(--nw-line)", borderRadius: "var(--nw-radius-control)" };
const SMALL_CONTROL = { display: "inline-flex", alignItems: "center", gap: 6, minHeight: 44, padding: "0 12px", background: "var(--nw-surface)", border: "1px solid var(--nw-line)", borderRadius: "var(--nw-radius-control)", fontSize: 14, fontWeight: 600, color: "var(--nw-ink)", boxShadow: "none" };

function FilterTile({ label, testId, value, onChange, children }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger
        data-testid={testId}
        style={{ ...CONTROL, height: "auto", flex: 1, minWidth: 0, padding: "0 12px", gap: 6, boxShadow: "none", fontSize: 14, color: "var(--nw-ink)" }}
      >
        <div style={{ display: "flex", flexDirection: "column", minWidth: 0, textAlign: "left" }}>
          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--nw-ink-muted)" }}>{label}</span>
          <div style={{ fontSize: 14, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}><SelectValue /></div>
        </div>
      </SelectTrigger>
      <SelectContent>{children}</SelectContent>
    </Select>
  );
}

function Notice({ icon, line, sub, action, testId }) {
  return (
    <div data-testid={testId} style={{ ...CARD, display: "flex", flexDirection: "column", alignItems: "center", gap: 10, padding: "28px 20px", textAlign: "center" }}>
      <div aria-hidden="true" style={{ width: 52, height: 52, borderRadius: 16, background: "var(--nw-navy-tint)", color: "var(--nw-navy)", display: "flex", alignItems: "center", justifyContent: "center" }}>{icon}</div>
      <div className="nw-body-strong">{line}</div>
      {sub && <div className="nw-lede">{sub}</div>}
      {action}
    </div>
  );
}

function FailedCard({ onRetry }) {
  return (
    <div data-testid="perf-load-error" style={{ ...CARD, display: "flex", flexDirection: "column", gap: 8, padding: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <CloudOff size={20} aria-hidden="true" style={{ color: "var(--nw-alert)", flexShrink: 0 }} />
        <div className="nw-body-strong">Couldn't load your matches</div>
      </div>
      <div className="nw-help">Nothing has been lost — we just couldn't reach the server.</div>
      <button
        type="button"
        data-testid="perf-load-error-retry"
        onClick={onRetry}
        style={{ ...SMALL_CONTROL, alignSelf: "flex-start", padding: "0 14px", border: "1px solid var(--nw-line-strong)", color: "var(--nw-navy)" }}
      >
        <RotateCw size={14} aria-hidden="true" />
        Try again
      </button>
    </div>
  );
}

function Skeleton() {
  return (
    <div data-testid="perf-loading" aria-busy="true" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {[0, 1, 2].map((i) => (
        <div key={i} style={{ ...CARD, display: "flex", flexDirection: "column", gap: 8, padding: 14, height: 120, boxSizing: "border-box" }}>
          <div style={{ width: "55%", height: 12, borderRadius: 6, background: "var(--nw-line)" }} />
          <div style={{ width: "80%", height: 10, borderRadius: 5, background: "var(--nw-line-faint)" }} />
        </div>
      ))}
    </div>
  );
}

export default function MatchesScreen({
  matches,
  sorted,
  loaded,
  loadFailed,
  q,
  setQ,
  comp,
  setComp,
  season,
  setSeason,
  competitions,
  seasonOptions,
  sort,
  setSort,
  sortColumns,
  onLog,
  onEdit,
  onDelete,
  onRetry,
  onClearFilters,
  desktopTable = null,
  children,
}) {
  const { setActive: setChromeless } = useChromelessOverride();
  const [openId, setOpenId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  // One way into the dialog (#1124, D221 O8): the cards' bin and the desktop
  // table's bin both open it here, never a native `confirm()`.
  const openDelete = (m) => { setDeleteError(null); setDeleteTarget(m); };

  useEffect(() => {
    setChromeless(true);
    return () => setChromeless(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const confirmDelete = async () => {
    setDeleting(true);
    setDeleteError(null);
    try {
      await onDelete(deleteTarget);
      setDeleteTarget(null);
    } catch {
      setDeleteError(DELETE_FAILED);
    } finally {
      setDeleting(false);
    }
  };

  const narrowed = !!q || comp !== "all" || season !== "all";
  const failed = loaded && loadFailed;
  const empty = loaded && !loadFailed && matches.length === 0;
  const filteredEmpty = loaded && !loadFailed && matches.length > 0 && sorted.length === 0;
  // Nothing to narrow while empty or failed; while loading the controls stay
  // where they are (the drawing's own words), the count waits for an answer.
  const showControls = !loaded || (!loadFailed && matches.length > 0);

  return (
    <div data-testid="performance-page" style={{ color: "var(--nw-ink)" }}>
      <span data-testid="matches-new-screen" className="sr-only" style={{ color: "var(--nw-ink)" }} aria-hidden="true" />
      <TopBar
        variant="white"
        homeHref="/dashboard"
        homeLabel="CricketCompass — go to your dashboard"
        endControls={
          <>
            <NotificationsBell compact surface="white" />
            <AccessibilityMenu compact surface="white" />
            <AccountMenu surface="white" hasRibbon />
          </>
        }
        data-testid="matches-topbar"
      />
      {/* D92's three chips, none current (Andreas, D206.5 Q1). */}
      <DestinationRow />

      <div className="flex flex-col" style={{ gap: "var(--nw-space-stack)", padding: "20px" }}>
        <div className="flex flex-col" style={{ gap: 6 }}>
          <div className="nw-eyebrow">Every match</div>
          <div data-focus-after-delete tabIndex={-1} style={{ outline: "none" }} className="nw-title">Your matches</div>
          <div className="nw-lede">{LEDE}</div>
        </div>

        <ActionButton data-testid="perf-log-btn" icon={<Plus size={20} />} onClick={onLog}>Log a match</ActionButton>

        {showControls && (
          <div className="flex flex-col" style={{ gap: 8 }}>
            <label style={{ ...CONTROL, display: "flex", alignItems: "center", gap: 10, padding: "0 12px" }}>
              <Search size={18} aria-hidden="true" style={{ color: "var(--nw-ink-muted)", flexShrink: 0 }} />
              <input
                data-testid="perf-search"
                type="search"
                aria-label="Search matches"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search opponent, team, ground…"
                className="nw-kit-input"
                style={{ flex: 1, minWidth: 0, minHeight: 44, border: "none", outline: "none", background: "transparent", fontSize: 15, color: "var(--nw-ink)" }}
              />
            </label>
            <div style={{ display: "flex", gap: 8 }}>
              <FilterTile label="Season" testId="perf-filter-season" value={season} onChange={setSeason}>
                <SelectItem value="all">All seasons</SelectItem>
                {seasonOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </FilterTile>
              <FilterTile label="Competition" testId="perf-filter-comp" value={comp} onChange={setComp}>
                {competitions.map((c) => <SelectItem key={c} value={c}>{c === "all" ? "All competitions" : c}</SelectItem>)}
              </FilterTile>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span data-testid="perf-count" className="nw-caption" style={{ flexGrow: 1, fontSize: 13, color: "var(--nw-ink-muted)" }}>
                {loaded ? countLine(sorted.length, matches.length, narrowed) : ""}
              </span>
              {/* Phone only: at `md:` the desktop table's own headers sort. */}
              <div className="md:hidden" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Select value={sort.key} onValueChange={(v) => setSort((s) => ({ key: v, dir: s.dir }))}>
                  <SelectTrigger data-testid="perf-sort-mobile-key" aria-label="Sort by" style={{ ...SMALL_CONTROL, width: "auto", height: "auto" }}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {sortColumns.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <button
                  type="button"
                  data-testid="perf-sort-mobile-dir"
                  data-nav="in-place"
                  onClick={() => setSort((s) => ({ ...s, dir: s.dir === "asc" ? "desc" : "asc" }))}
                  style={{ ...SMALL_CONTROL, fontSize: 13, color: "var(--nw-navy)" }}
                >
                  {sort.dir === "asc" ? <ArrowUp size={14} aria-hidden="true" /> : <ArrowDown size={14} aria-hidden="true" />}
                  {sortDirLabel(sort.key, sort.dir)}
                </button>
              </div>
            </div>
          </div>
        )}

        {!loaded && <Skeleton />}
        {failed && <FailedCard onRetry={onRetry} />}
        {empty && (
          <Notice
            testId="perf-empty"
            icon={<Search size={22} aria-hidden="true" />}
            line={EMPTY_LINE}
          />
        )}
        {filteredEmpty && (
          <Notice
            testId="perf-filtered-empty"
            icon={<Search size={22} aria-hidden="true" />}
            line={FILTERED_EMPTY_LINE}
            action={
              <button type="button" data-testid="perf-clear-filters" onClick={onClearFilters} style={{ minHeight: 44, padding: "0 14px", background: "none", border: "none", fontSize: 13, fontWeight: 600, color: "var(--nw-navy)", textDecoration: "underline", cursor: "pointer" }}>
                Clear the filters
              </button>
            }
          />
        )}
        {/* The redraw is phone-only (decision 19): below `md` the folded cards,
            at `md:` and up the shipped table, passed in by the page as a function of the
            delete opener (#1124), so its bin opens the same dialog as the cards. */}
        {loaded && !loadFailed && sorted.length > 0 && (
          <div className="md:hidden flex flex-col" style={{ gap: "var(--nw-space-stack)" }}>
            <div data-testid="matches-cards" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {sorted.map((m) => (
                <MatchCardFold
                  key={m.match_id}
                  m={m}
                  open={openId === m.match_id}
                  onToggle={() => setOpenId((cur) => (cur === m.match_id ? null : m.match_id))}
                  onEdit={() => onEdit(m)}
                  onDelete={() => openDelete(m)}
                />
              ))}
            </div>
            <div className="nw-help">A dash is a figure that was never entered. A match you did not bowl in reads a dash, not a nought.</div>
          </div>
        )}
        {loaded && !loadFailed && desktopTable && desktopTable(openDelete)}
        {children}
      </div>

      <DeleteMatchDialog
        target={deleteTarget}
        deleting={deleting}
        error={deleteError}
        onCancel={() => { setDeleteTarget(null); setDeleteError(null); }}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
